<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Shared from '@/common/shared'
import moment from 'moment'
import { useDropzone } from 'vue3-dropzone'
import { useToast } from 'vue-toastification'
import { Dictionary, FileDocument, FullAssetVersion, MySelf, Regions } from '@/common/api.service'
import { isVersionFieldChanged, isVersionTotalFieldChanged, normalizeVersionDifferences } from '@/components/Capitalization/versionDifferences'

const toast = useToast()

const route = useRoute()
const router = useRouter()
const list = ref([])
const years = ref([])
const currentYear = new Date().getFullYear()
const filterYear = ref(route.query.year ? parseYear(route.query.year) : currentYear)
const filterVersion = ref(route.query.version ? String(route.query.version) : '')
const filterRegion = ref(parseRegionId(route.params.id))
const selectedYear = ref(filterYear.value)
const versions = ref([])
const versionsLoaded = ref(false)
const selectedVersion = ref(filterVersion.value)
const loaded = ref(false)
const isInitialized = ref(false)
const versionData = ref({
  capitalization: { rows: [], total: {}, year: null },
  budget: { years: [], requestedYear: null },
})
const permissions = ref([])
const accepting = ref(false)
const rejecting = ref(false)
const deleting = ref(false)
const creating = ref(false)
const downloading = ref(false)
const submitting = ref(false)
const submitComment = ref('')
const submitDocFile = ref()
const submitDragStarted = ref(false)
const submitDbl = ref(0)
const versionHistory = ref([])
const historyLoading = ref(false)
const versionDifferences = ref(new Set())
const isRegionalFundEmployee = ref(false)
const idRegionalFundEmployee = ref(0)
const regionActive = ref(null)
let isComponentActive = false

onMounted(() => {
  isComponentActive = true
  initialize()
})

onBeforeUnmount(() => {
  isComponentActive = false
  isInitialized.value = false
})

function isViewRouteActive() {
  return isComponentActive && route.name === 'CapitalizationView'
}

async function safeReplace(routeLocation) {
  if (!isViewRouteActive()) return
  await router.replace(routeLocation)
}

const VERSION_STATUS_SUBMITTED_FOR_REVIEW = 2
const VERSION_STATUS_APPROVED = 3
const VERSION_STATUS_REJECTED = 4

const VERSION_STATUS_NAMES = {
  0: 'Черновик',
  1: 'Черновик',
  2: 'Направлена на рассмотрение',
  3: 'Согласована',
  4: 'Отклонена',
  5: 'Сохранено'
}

const canAcceptVersion = computed(() => {
  return !!permission('/funds/asset/versions/accept:accepting')
    && versionData.value.versionStatus === VERSION_STATUS_SUBMITTED_FOR_REVIEW
})

const canRejectVersion = computed(() => {
  return !!permission('/funds/asset/versions/reject:rejecting')
    && versionData.value.versionStatus === VERSION_STATUS_SUBMITTED_FOR_REVIEW
})

const canEditVersion = computed(() => {
  if (!permission('/funds/asset/versions:update') || !selectedVersion.value) {
    return false
  }
  const status = currentVersionStatus.value
  if (status === VERSION_STATUS_REJECTED) {
    return false
  }
  if (isRegionalFundEmployee.value) {
    return status != null && status <= 1
  }
  return true
})

const canDeleteVersion = computed(() => {
  if (!permission('/funds/asset/versions:delete') || !selectedVersion.value) {
    return false
  }
  if (isRegionalFundEmployee.value) {
    const status = currentVersionStatus.value
    return status != null && status <= 1
  }
  return true
})

const canCreateVersion = computed(() => {
  const status = currentVersionStatus.value
  return !!permission('/funds/asset/versions:create')
    && !!selectedFundId.value
    && !!selectedYear.value
    && (status == null || status > 1)
})

const canSubmitVersion = computed(() => {
  const status = currentVersionStatus.value
  return !!permission('/funds/asset/versions/submit:submitting')
    && !!selectedVersion.value
    && status != null
    && status <= 1
})

const canDownloadReport = computed(() => {
  const status = currentVersionStatus.value
  return !!selectedVersion.value
    && status != null
    && status >= VERSION_STATUS_SUBMITTED_FOR_REVIEW
})

const { getRootProps: getSubmitRootProps, getInputProps: getSubmitInputProps } = useDropzone({
  onDrop: onSubmitDrop,
  accept: ['.pdf'],
})

function permission(permissionName) {
  if (!permissions.value.find((p) => p === permissionName)) {
    console.warn('no permission', permissionName)
  }
  return permissions.value.find((p) => p === permissionName)
}

function parseRegionId(id) {
  if (!id) return ''
  const num = Number(id)
  return Number.isNaN(num) ? id : num
}

function parseYear(year) {
  if (!year) return ''
  const num = Number(year)
  return Number.isNaN(num) ? year : num
}

function getDefaultYear() {
  const defaultYear = years.value.find(year => year.value === currentYear)
  if (defaultYear) return defaultYear.value
  if (years.value.length > 0) return years.value[0].value
  return currentYear
}

function applyFirstVersionIfNeeded(data) {
  if (selectedVersion.value || data.length === 0) {
    return false
  }
  const firstVersionId = String(data[0].versionId)
  selectedVersion.value = firstVersionId
  filterVersion.value = firstVersionId
  return true
}

function capitalizationViewRoute(regionId, year, version) {
  const query = {}
  if (year) {
    query.year = year
  }
  if (version) {
    query.version = version
  }
  return { name: 'CapitalizationView', params: { id: regionId }, query }
}

function syncFiltersFromRoute() {
  filterRegion.value = parseRegionId(route.params.id)
  filterYear.value = route.query.year ? parseYear(route.query.year) : filterYear.value
  filterVersion.value = route.query.version ? String(route.query.version) : ''
  selectedRegion.value = filterRegion.value
  selectedYear.value = filterYear.value
  selectedVersion.value = filterVersion.value
}

async function applyFilters() {
  if (!isViewRouteActive()) return

  const nextRegion = filterRegion.value
  const nextYear = filterYear.value
  const nextVersion = filterVersion.value ? String(filterVersion.value) : ''
  const regionChanged = String(nextRegion) !== String(route.params.id)
  const queryChanged = String(nextYear) !== String(route.query.year || '')
    || String(nextVersion) !== String(route.query.version || '')

  selectedRegion.value = nextRegion
  selectedYear.value = nextYear
  selectedVersion.value = nextVersion

  if (regionChanged) {
    await router.push(capitalizationViewRoute(nextRegion, nextYear, nextVersion))
    return
  }
  if (queryChanged) {
    await router.replace(capitalizationViewRoute(route.params.id, nextYear, nextVersion))
    return
  }
  await fetchVersions()
}

const selectedRegion = ref(filterRegion.value)

const yearsList = computed(() => {
  const r = []
  years.value.forEach(function (year) {
    r.push({ value: year.value, label: year.title })
  })
  return r
})

const versionsList = computed(() => {
  return versions.value.map(function (version) {
    return {
      value: version.versionId,
      label: version.versionCode ? `Версия ${version.versionCode}` : `Версия ${version.versionId}`,
    }
  })
})

const selectedFundId = computed(() => {
  const region = list.value.find(r => String(r.id) === String(selectedRegion.value))
  return region?.fund?.id ?? ''
})

const filterFundId = computed(() => {
  const region = list.value.find(r => String(r.id) === String(filterRegion.value))
  return region?.fund?.id ?? ''
})

const regionList = computed(() => {
  const r = []
  list.value.forEach(function (f) {
    r.push({ value: f.id, label: `${f.title}` })
  })
  return r
})

const selectedRegionTitle = computed(() => {
  const region = list.value.find(r => String(r.id) === String(selectedRegion.value))
  return region ? region.title : ''
})

const capitalization = computed(() => versionData.value.capitalization?.rows ?? [])
const capitalizationTotal = computed(() => versionData.value.capitalization?.total ?? {})
const budgetYearData = computed(() => {
  const years = versionData.value.budget?.years ?? []
  return years.find(year => year.year === Number(selectedYear.value)) ?? years[0] ?? { rows: [], total: {} }
})
const budget = computed(() => budgetYearData.value.rows ?? [])
const budgetTotal = computed(() => budgetYearData.value.total ?? {})

const emptyStateMessage = computed(() => {
  if (versions.value.length === 0) {
    return 'По данному году версий не создано'
  }
  return 'Выберите версию для отображения'
})

const selectedVersionInfo = computed(() => {
  return versions.value.find(version => String(version.versionId) === String(selectedVersion.value))
})

const currentVersionStatus = computed(() => {
  if (!selectedVersion.value) return null
  if (versionData.value.versionStatus != null) {
    return versionData.value.versionStatus
  }
  return selectedVersionInfo.value?.versionStatus ?? null
})

const versionStatusText = computed(() => {
  const status = currentVersionStatus.value
  if (status == null) return ''
  return VERSION_STATUS_NAMES[status] ?? ''
})

const versionStatusNote = computed(() => {
  if (canEditVersion.value) return ''

  const status = currentVersionStatus.value
  if (status == null) return ''

  if (status === VERSION_STATUS_APPROVED) {
    return 'Вы просматриваете версию в статусе «Согласована». Редактирование недоступно.'
  }
  if (status === VERSION_STATUS_REJECTED) {
    return 'Вы просматриваете версию в статусе «Отклонена». Редактирование недоступно.'
  }
  if (status === VERSION_STATUS_SUBMITTED_FOR_REVIEW) {
    return 'Версия направлена на рассмотрение. Редактирование недоступно.'
  }
  return ''
})

const downloadReportSubmitText = computed(() => {
  return versionData.value.submitText ?? selectedVersionInfo.value?.submitText ?? ''
})

const downloadReportVersionLabel = computed(() => {
  const version = selectedVersionInfo.value
  if (!version) return ''

  const code = version.versionCode ?? version.versionId
  const date = versionData.value.createdAt ?? version.createdAt ?? version.modifiedAt
  if (!date) {
    return `Версия ${code}.`
  }
  return `Версия ${code} от ${moment(date).format('DD.MM.YYYY')}`
})

function historyStatusName(status) {
  return VERSION_STATUS_NAMES[status] ?? ''
}

function isFieldChanged(code, field) {
  return isVersionFieldChanged(versionDifferences.value, code, field)
}

function isTotalFieldChanged(code, field) {
  return isVersionTotalFieldChanged(versionDifferences.value, code, field)
}

function rowLevel(row) {
  if (row.level == null || row.level === '') return null
  const level = Number(row.level)
  return Number.isNaN(level) ? null : level
}

function isRowEditable(rows, index) {
  const level = rowLevel(rows[index])
  if (level == null) return true
  if (index + 1 >= rows.length) return true
  const nextLevel = rowLevel(rows[index + 1])
  if (nextLevel == null) return true
  return nextLevel <= level
}

function findFirstNonRejectedVersion() {
  return versions.value.find(version => version.versionStatus !== VERSION_STATUS_REJECTED)
}

function buildVersionCreatePayload(data, year) {
  const capitalizationRows = data.capitalization?.rows ?? []
  const budgetYears = data.budget?.years ?? []
  const yearData = budgetYears.find(item => item.year === Number(year)) ?? budgetYears[0] ?? { rows: [] }
  const budgetRows = yearData.rows ?? []

  return {
    capitalizationRows: capitalizationRows
      .filter((row, index) => isRowEditable(capitalizationRows, index))
      .map(row => ({
        code: row.code,
        quarter1: row.quarter1 ?? null,
        quarter2: row.quarter2 ?? null,
        quarter3: row.quarter3 ?? null,
        quarter4: row.quarter4 ?? null,
      })),
    budgetRows: budgetRows.map(row => ({
      code: row.code,
      value: row.value ?? 0,
    })),
  }
}

async function getVersionCreatePayload(fundId, year) {
  const sourceVersion = findFirstNonRejectedVersion()
  if (!sourceVersion) {
    return {}
  }

  try {
    const { data } = await FullAssetVersion.item(fundId, year, sourceVersion.versionId)
    return buildVersionCreatePayload(data, year)
  } catch {
    return {}
  }
}

function onSubmitDrop(acceptFiles) {
  submitDocFile.value = acceptFiles[0]
  submitDragStarted.value = false
}

function onSubmitDragEnter() {
  submitDragStarted.value = true
}

function onSubmitDragEnd() {
  submitDragStarted.value = false
}

function removeSubmitDocFile() {
  submitDocFile.value = null
}

function resetSubmitForm() {
  submitComment.value = ''
  submitDocFile.value = null
  submitDragStarted.value = false
}

function showSubmitModal() {
  resetSubmitForm()
  Shared.showModal('#submit-version')
}

function closeSubmitModal() {
  Shared.closeModal('#submit-version')
  resetSubmitForm()
}

function showActionError(error, fallbackMessage) {
  if (error.response?.status === 400) {
    toast.error(error.response.data?.title || fallbackMessage, {
      timeout: 5000,
    })
  }
}

function getReportDownloadFilename(headers) {
  const disposition = headers?.['content-disposition']
  if (disposition) {
    const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i)
    if (utfMatch?.[1]) {
      try {
        return decodeURIComponent(utfMatch[1])
      } catch {
        return utfMatch[1]
      }
    }
    const match = disposition.match(/filename="?([^";]+)"?/i)
    if (match?.[1]) {
      return match[1]
    }
  }
  const region = selectedRegionTitle.value || 'РФРП'
  return `Отчет_капитализация_${region}_${selectedYear.value}.xlsx`
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

function openBlobInNewTab(blob, newTab = null) {
  const url = window.URL.createObjectURL(blob)
  if (newTab) {
    newTab.location.href = url
  } else {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000)
}

async function handleReportFetchError(error, newTab = null) {
  newTab?.close()
  if (error.response?.status === 400 && error.response.data instanceof Blob) {
    try {
      const payload = JSON.parse(await error.response.data.text())
      toast.error(payload?.title || 'Не удалось скачать отчет', { timeout: 5000 })
      return
    } catch {
      // fall through to generic error
    }
  }
  showActionError(error, 'Не удалось скачать отчет')
}

async function openReport() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  if (!fundId || !year || !versionId || downloading.value) {
    return
  }

  const newTab = window.open('about:blank', '_blank')
  downloading.value = true
  try {
    const response = await FullAssetVersion.download(fundId, year, versionId, {})
    openBlobInNewTab(response.data, newTab)
  } catch (error) {
    await handleReportFetchError(error, newTab)
  } finally {
    downloading.value = false
  }
}

async function downloadReport() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  if (!fundId || !year || !versionId || downloading.value) {
    return
  }

  downloading.value = true
  try {
    const response = await FullAssetVersion.download(fundId, year, versionId, {})
    triggerBlobDownload(response.data, getReportDownloadFilename(response.headers))
    Shared.closeModal('#download-report')
  } catch (error) {
    await handleReportFetchError(error)
  } finally {
    downloading.value = false
  }
}

function showDownloadReportModal() {
  if (!canDownloadReport.value) {
    return
  }
  Shared.showModal('#download-report')
}

async function submitVersion() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  const comment = submitComment.value.trim()

  if (!fundId || !year || !versionId || submitting.value) {
    return
  }

  if (!comment) {
    submitDbl.value++
    toast.error('Заполните обязательные поля', { timeout: 5000 })
    return
  }

  if (!submitDocFile.value || !submitDocFile.value.name) {
    submitDbl.value++
    toast.error('Заполните обязательные поля и загрузите документ', { timeout: 5000 })
    return
  }

  if (submitDocFile.value.type !== 'application/pdf') {
    submitDbl.value++
    toast.error('К загрузке доступен только файл в формате "pdf"', { timeout: 5000 })
    return
  }

  submitting.value = true
  try {
    const formData = new FormData()
    formData.append('data', submitDocFile.value)
    formData.append('name', submitDocFile.value.name)
    formData.append('mimeType', submitDocFile.value.type)

    const { data: fileData } = await FileDocument.fileTemp(formData)
    await FullAssetVersion.submit(fundId, year, versionId, {
      comment,
      temporaryFileId: fileData.id,
      name: fileData.name,
    })
    closeSubmitModal()
    await fetchVersions()
  } catch (error) {
    submitDbl.value++
    showActionError(error, 'Не удалось направить версию на рассмотрение')
  } finally {
    submitting.value = false
  }
}

async function showVersionHistory() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  if (!fundId || !year || !versionId || historyLoading.value) {
    return
  }

  historyLoading.value = true
  versionHistory.value = []
  try {
    const { data } = await FullAssetVersion.history(fundId, year, versionId)
    versionHistory.value = data
    Shared.showModal('#version-history')
  } catch {
    versionHistory.value = []
  } finally {
    historyLoading.value = false
  }
}

async function fetchFilterVersions() {
  if (!isComponentActive) return

  const fundId = filterFundId.value
  const year = filterYear.value
  if (!fundId || !year) {
    versions.value = []
    return
  }

  try {
    const { data } = await FullAssetVersion.list(fundId, year)
    if (!isComponentActive) return
    versions.value = data
    if (filterVersion.value && !data.find(version => String(version.versionId) === String(filterVersion.value))) {
      filterVersion.value = ''
    }
  } catch {
    if (!isComponentActive) return
    versions.value = []
    filterVersion.value = ''
  }
}

async function fetchVersions() {
  if (!isComponentActive) return

  const fundId = selectedFundId.value
  const year = selectedYear.value
  versionsLoaded.value = false
  if (!fundId || !year) {
    versions.value = []
    selectedVersion.value = ''
    versionsLoaded.value = true
    await fetchVersionData()
    return
  }

  try {
    const { data } = await FullAssetVersion.list(fundId, year)
    if (isComponentActive) {
      versions.value = data
      if (selectedVersion.value && !data.find(version => String(version.versionId) === String(selectedVersion.value))) {
        selectedVersion.value = ''
        filterVersion.value = ''
      }
      if (applyFirstVersionIfNeeded(data)) {
        const routeVersion = route.query.version ? String(route.query.version) : ''
        if (routeVersion !== selectedVersion.value) {
          await safeReplace(capitalizationViewRoute(selectedRegion.value, year, selectedVersion.value))
        }
      }
    }
  } catch {
    if (isComponentActive) {
      versions.value = []
      selectedVersion.value = ''
    }
  } finally {
    if (isComponentActive) {
      versionsLoaded.value = true
    }
  }

  if (!isComponentActive) return
  await fetchVersionData()
}

async function fetchVersionDifferences() {
  if (!isComponentActive) return

  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  versionDifferences.value = new Set()
  if (!fundId || !year || !versionId) {
    return
  }

  try {
    const { data } = await FullAssetVersion.differences(fundId, year, versionId)
    if (!isComponentActive) return
    versionDifferences.value = normalizeVersionDifferences(data)
  } catch {
    if (!isComponentActive) return
    versionDifferences.value = new Set()
  }
}

async function fetchVersionData() {
  if (!isComponentActive) return

  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  versionDifferences.value = new Set()
  if (!fundId || !year || !versionId) {
    versionData.value = {
      capitalization: { rows: [], total: {}, year: null },
      budget: { years: [], requestedYear: null },
    }
    loaded.value = false
    return
  }

  loaded.value = false
  try {
    const [{ data }] = await Promise.all([
      FullAssetVersion.item(fundId, year, versionId),
      fetchVersionDifferences(),
    ])
    if (!isComponentActive) return
    versionData.value = data
    loaded.value = true
  } catch {
    if (!isComponentActive) return
    versionData.value = {
      capitalization: { rows: [], total: {}, year: null },
      budget: { years: [], requestedYear: null },
    }
    loaded.value = false
    versionDifferences.value = new Set()
  }
}

function goToEdit() {
  router.push({
    name: 'CapitalizationEdit',
    params: { id: selectedRegion.value },
    query: {
      year: selectedYear.value,
      version: selectedVersion.value,
    },
  })
}

function showCreateModal() {
  Shared.showModal('#create-version')
}

async function createVersion() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  if (!fundId || !year || creating.value) {
    return
  }

  creating.value = true
  try {
    const createPayload = await getVersionCreatePayload(fundId, year)
    const { data } = await FullAssetVersion.create(fundId, year, createPayload)
    Shared.closeModal('#create-version')
    router.push({
      name: 'CapitalizationEdit',
      params: { id: selectedRegion.value },
      query: {
        year,
        version: data.versionId,
      },
    })
  } catch (error) {
    showActionError(error, 'Не удалось создать версию')
  } finally {
    creating.value = false
  }
}

function showAcceptModal() {
  Shared.showModal('#accept-version')
}

async function acceptVersion() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  if (!fundId || !year || !versionId || accepting.value) {
    return
  }

  accepting.value = true
  try {
    await FullAssetVersion.accept(fundId, year, versionId)
    Shared.closeModal('#accept-version')
    await fetchVersions()
  } catch (error) {
    showActionError(error, 'Не удалось согласовать версию')
  } finally {
    accepting.value = false
  }
}

function showRejectModal() {
  Shared.showModal('#reject-version')
}

async function rejectVersion() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  if (!fundId || !year || !versionId || rejecting.value) {
    return
  }

  rejecting.value = true
  try {
    await FullAssetVersion.reject(fundId, year, versionId)
    Shared.closeModal('#reject-version')
    await fetchVersions()
  } catch (error) {
    showActionError(error, 'Не удалось отклонить версию')
  } finally {
    rejecting.value = false
  }
}

function showDeleteModal() {
  Shared.showModal('#delete-version')
}

async function deleteVersion() {
  const fundId = selectedFundId.value
  const year = selectedYear.value
  const versionId = selectedVersion.value
  if (!fundId || !year || !versionId || deleting.value) {
    return
  }

  deleting.value = true
  try {
    await FullAssetVersion.remove(fundId, year, versionId)
    Shared.closeModal('#delete-version')
    selectedVersion.value = ''
    filterVersion.value = ''
    await router.replace(capitalizationViewRoute(selectedRegion.value, selectedYear.value, ''))
    await fetchVersions()
  } catch (error) {
    showActionError(error, 'Не удалось удалить версию')
  } finally {
    deleting.value = false
  }
}

watch(filterYear, async (year, oldYear) => {
  const changed = oldYear !== undefined && String(year) !== String(oldYear)
  if (changed) {
    const hadVersion = !!filterVersion.value
    filterVersion.value = ''
    if (isInitialized.value) {
      await fetchFilterVersions()
      if (!hadVersion) {
        await applyFilters()
      }
    }
    return
  }
  if (isInitialized.value) {
    await fetchFilterVersions()
  }
})

watch(filterRegion, async (region, oldRegion) => {
  const changed = oldRegion !== undefined && String(region) !== String(oldRegion)
  if (changed) {
    const hadVersion = !!filterVersion.value
    filterVersion.value = ''
    if (isInitialized.value) {
      await fetchFilterVersions()
      if (!hadVersion) {
        await applyFilters()
      }
    }
    return
  }
  if (isInitialized.value) {
    await fetchFilterVersions()
  }
})

watch(filterVersion, async (version, oldVersion) => {
  if (!isInitialized.value || oldVersion === undefined) return
  if (String(version) === String(oldVersion)) return
  await applyFilters()
})

watch(() => [route.params.id, route.query.year, route.query.version], async () => {
  if (!isInitialized.value || !isViewRouteActive()) return
  syncFiltersFromRoute()
  await fetchVersions()
})

async function initialize() {
  if (!isComponentActive) return

  try {
    const { data } = await Dictionary.fundCapitalizationYears()
    if (!isComponentActive) return
    years.value = data
    if (route.query.year) {
      filterYear.value = parseYear(route.query.year)
      selectedYear.value = filterYear.value
    } else {
      const year = getDefaultYear()
      filterYear.value = year
      selectedYear.value = year
      if (String(route.query.year || '') !== String(year)) {
        await safeReplace(capitalizationViewRoute(route.params.id, year, route.query.version))
      }
    }
  } catch {
    return
  }

  if (!isComponentActive) return

  try {
    const { data } = await Regions.permissions()
    if (!isComponentActive) return
    permissions.value = data
  } catch {
    return
  }

  if (!isComponentActive) return

  try {
    const { data } = await MySelf.card()
    if (!isComponentActive) return
    if (data.isRegionalFundEmployee) {
      isRegionalFundEmployee.value = true
      idRegionalFundEmployee.value = data.relatedRegionId
    }
  } catch {
    return
  }

  if (!isComponentActive) return

  if (!isRegionalFundEmployee.value) {
    try {
      const { data } = await Regions.list({})
      if (!isComponentActive) return
      list.value = data
      syncFiltersFromRoute()
      isInitialized.value = true
      await fetchVersions()
    } catch {
      return
    }
  } else {
    try {
      const { data } = await Regions.item(idRegionalFundEmployee.value)
      if (!isComponentActive) return
      list.value = [data]
      regionActive.value = data
      const regionId = parseRegionId(data.id)
      filterRegion.value = regionId
      selectedRegion.value = regionId
      if (String(route.params.id) !== String(regionId)) {
        await safeReplace(capitalizationViewRoute(regionId, selectedYear.value, route.query.version))
      }
      syncFiltersFromRoute()
      isInitialized.value = true
      await fetchVersions()
    } catch {
      return
    }
  }
}
</script>

<template>
  <div class="wrapper cap-main-wrapper">
    <MainHeader></MainHeader>
    <main class="page page--js">
      <div class="breadcrumbs-container">
        <div class="container">
          <div class="breadcrumbs-wrapper cap-breadcrumbs">
            <ul class="breadcrumbs">
              <li>Модуль регионов</li>
              <li v-if="!isRegionalFundEmployee">
                <router-link :to="{ name: 'ListRegions' }">Список регионов</router-link>
              </li>
              <li v-else>Список регионов</li>
              <li v-if="isRegionalFundEmployee && regionActive">
                <router-link :to="{ name: 'CardRegions', params: { id: regionActive.id } }">{{ regionActive.title }}</router-link>
              </li>
              <li v-else-if="selectedRegionTitle">
                <router-link :to="{ name: 'CardRegions', params: { id: selectedRegion } }">{{ selectedRegionTitle }}</router-link>
              </li>
              <li>Капитализация РФРП</li>
            </ul>
            <div class="cap-breadcrumbs__actions">
              <button type="button" class="button button-download-report" :disabled="!canDownloadReport" @click="showDownloadReportModal">Скачать отчет</button>
            </div>
          </div>
        </div>
      </div>

      <div class="filters-container cap-filters-container">
        <div class="container">
          <div class="filters cap-filters">
            <div class="filters__body cap-filters__body">
              <Multiselect class="field field--select field--m-width-265 margin-0" v-model="filterYear"
                           mode="single" :options="yearsList" :show-no-results="false" :searchable="true"
                           placeholder="Год" :no-options-text="'Укажите год'" />
              <Multiselect class="field field--select field--m-width-265 margin-0" v-model="filterVersion"
                           mode="single" :options="versionsList" :show-no-results="false" placeholder="Версия" />
              <Multiselect v-if="!isRegionalFundEmployee" class="field field--select field--m-width-265 margin-0" v-model="filterRegion"
                           mode="single" :options="regionList" :show-no-results="false" :searchable="true"
                           placeholder="Название региона" :no-options-text="'Укажите регион'" />
            </div>
            <div class="cap-status" v-if="versionStatusText">
              <button type="button" class="cap-history-btn" title="История" @click="showVersionHistory"></button>
              <span class="cap-status__text">
                Статус версии: {{ versionStatusText }}
                <span class="cap-status__note" v-if="versionStatusNote">{{ versionStatusNote }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="content content-white" v-if="loaded">
        <div class="container">
          <div class="table-responsive table-responsive--bg-color-white">
            <table class="table capitalisation">
              <thead>
              <tr>
                <th>Показатель</th>
                <th colspan="4">Значение</th>
              </tr>
              </thead>
              <tbody>
              <tr class="title-row">
                <td>1. КАПИТАЛИЗАЦИЯ</td>
                <td colspan="4">По состоянию на {{ versionData.capitalization?.year || selectedYear }} год</td>
              </tr>
              <tr class="cap-dates-row">
                <td></td>
                <td>31.03</td>
                <td>30.06</td>
                <td>30.09</td>
                <td>31.12</td>
              </tr>
              <tr v-for="(row, index) in capitalization" :key="index" :class="{
                    'table-first-level': row.level === 1,
                    'table-second-level': row.level === 2,
                    'table-third-level': row.level === 3,
                  }">
                <td class="title-td">{{ row.code }} {{ row.title }}</td>
                <td>
                  <span v-if="isFieldChanged(row.code, 'quarter1')" class="cap-value-changed">{{ $filters.numberFormat(row.quarter1, 2) }}</span>
                  <template v-else>{{ $filters.numberFormat(row.quarter1, 2) }}</template>
                </td>
                <td>
                  <span v-if="isFieldChanged(row.code, 'quarter2')" class="cap-value-changed">{{ $filters.numberFormat(row.quarter2, 2) }}</span>
                  <template v-else>{{ $filters.numberFormat(row.quarter2, 2) }}</template>
                </td>
                <td>
                  <span v-if="isFieldChanged(row.code, 'quarter3')" class="cap-value-changed">{{ $filters.numberFormat(row.quarter3, 2) }}</span>
                  <template v-else>{{ $filters.numberFormat(row.quarter3, 2) }}</template>
                </td>
                <td>
                  <span v-if="isFieldChanged(row.code, 'quarter4')" class="cap-value-changed">{{ $filters.numberFormat(row.quarter4, 2) }}</span>
                  <template v-else>{{ $filters.numberFormat(row.quarter4, 2) }}</template>
                </td>
              </tr>
              <tr class="total-row">
                <td>{{ capitalizationTotal.code }} {{ capitalizationTotal.title }}</td>
                <td>
                  <span v-if="isTotalFieldChanged(capitalizationTotal.code, 'quarter1')" class="cap-value-changed">{{ $filters.numberFormat(capitalizationTotal.quarter1, 2) }}</span>
                  <template v-else>{{ $filters.numberFormat(capitalizationTotal.quarter1, 2) }}</template>
                </td>
                <td>
                  <span v-if="isTotalFieldChanged(capitalizationTotal.code, 'quarter2')" class="cap-value-changed">{{ $filters.numberFormat(capitalizationTotal.quarter2, 2) }}</span>
                  <template v-else>{{ $filters.numberFormat(capitalizationTotal.quarter2, 2) }}</template>
                </td>
                <td>
                  <span v-if="isTotalFieldChanged(capitalizationTotal.code, 'quarter3')" class="cap-value-changed">{{ $filters.numberFormat(capitalizationTotal.quarter3, 2) }}</span>
                  <template v-else>{{ $filters.numberFormat(capitalizationTotal.quarter3, 2) }}</template>
                </td>
                <td>
                  <span v-if="isTotalFieldChanged(capitalizationTotal.code, 'quarter4')" class="cap-value-changed">{{ $filters.numberFormat(capitalizationTotal.quarter4, 2) }}</span>
                  <template v-else>{{ $filters.numberFormat(capitalizationTotal.quarter4, 2) }}</template>
                </td>
              </tr>

              <tr class="title-row cap-section-gap">
                <td>2. БЮДЖЕТ на выдачу займов, в т.ч. план на текущий год</td>
                <td colspan="4">{{ selectedYear }} год</td>
              </tr>
              <tr v-for="(row, index) in budget" :key="'b' + index">
                <td class="title-td">{{ row.code }} {{ row.title }}</td>
                <td colspan="4">
                  <span v-if="isFieldChanged(row.code, 'value')" class="cap-value-changed">{{ $filters.numberFormat(row.value, 2) }}</span>
                  <template v-else>{{ $filters.numberFormat(row.value, 2) }}</template>
                </td>
              </tr>
              <tr class="total-row" v-if="budgetTotal.title">
                <td>{{ budgetTotal.title }}</td>
                <td colspan="4">
                  <span v-if="isTotalFieldChanged(budgetTotal.code, 'value')" class="cap-value-changed">{{ $filters.numberFormat(budgetTotal.value, 2) }}</span>
                  <template v-else>{{ $filters.numberFormat(budgetTotal.value, 2) }}</template>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="content content-white cap-empty-state" v-else-if="versionsLoaded && !selectedVersion">
        <div class="container">
          <p class="cap-empty-state__text">{{ emptyStateMessage }}</p>
        </div>
      </div>

      <div class="cap-bottom-bar" v-if="loaded || (versionsLoaded && canCreateVersion)">
        <div class="container cap-bottom-bar__inner">
          <button type="button" class="button button--accept" v-if="canAcceptVersion" @click="showAcceptModal">Акцептовать</button>
          <button type="button" class="button button--reject" v-if="canRejectVersion" @click="showRejectModal">Отклонить</button>
          <button type="button" class="button" v-if="canSubmitVersion" @click="showSubmitModal">Направить на рассмотрение</button>
          <button type="button" class="button" v-if="canEditVersion" @click="goToEdit">Редактировать</button>
          <button type="button" class="button button--accept" v-if="canCreateVersion" @click="showCreateModal">Создать версию</button>
          <button type="button" class="button button--delete" v-if="canDeleteVersion" @click="showDeleteModal">Удалить версию</button>
        </div>
      </div>

      <div class="modal-overlay" id="create-version">
        <div class="popup">
          <div class="modal-title title-h3">Создать версию</div>
          <div class="modal-body">Вы действительно хотите создать новую версию?</div>
          <div class="modal-bottom">
            <div class="buttons-block">
              <button type="button" class="button button--clear" @click="Shared.closeModal('#create-version')">Отменить</button>
              <button type="button" class="button" :disabled="creating" @click="createVersion">Создать</button>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-overlay" id="accept-version">
        <div class="popup">
          <div class="modal-title title-h3">Акцептовать версию</div>
          <div class="modal-body">Вы действительно хотите акцептовать версию?</div>
          <div class="modal-bottom">
            <div class="buttons-block">
              <button type="button" class="button button--clear" @click="Shared.closeModal('#accept-version')">Отменить</button>
              <button type="button" class="button" :disabled="accepting" @click="acceptVersion">Акцептировать</button>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-overlay" id="reject-version">
        <div class="popup">
          <div class="modal-title title-h3">Отклонить версию</div>
          <div class="modal-body">Вы действительно хотите отклонить версию?</div>
          <div class="modal-bottom">
            <div class="buttons-block">
              <button type="button" class="button button--clear" @click="Shared.closeModal('#reject-version')">Отменить</button>
              <button type="button" class="button" :disabled="rejecting" @click="rejectVersion">Отклонить</button>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-overlay" id="submit-version" @dragenter="onSubmitDragEnter" @dragleave="onSubmitDragEnd">
        <div class="popup cap-popup">
          <div class="modal-title title-h3">Направить версию на рассмотрение</div>
          <div class="modal-body">
            <label class="title-field">
              <span class="title-field-name">Комментарий<span class="cap-required">*</span></span>
              <textarea
                  v-model="submitComment"
                  class="field cap-modal-comment cap-modal-comment-dash"
                  rows="4"
                  maxlength="2000"
                  placeholder="До 2000 знаков"></textarea>
            </label>
            <div class="title-field title-field--relative cap-modal-file-labell">
              <span class="title-field-name">Файл документа (Формат файла PDF) <span class="cap-required">*</span></span>
              <div class="title-field__doc" v-if="submitDocFile?.name">
                <span style="margin-right: 5px">{{ submitDocFile.name }}</span>
                <button type="button" class="button-del" style="display: inline" @click.stop="removeSubmitDocFile"></button>
              </div>
              <div v-else
                   class="label-add-file transparent"
                   :class="{ 'drag-started': submitDragStarted }"
                   v-bind="getSubmitRootProps()">
                <input v-bind="getSubmitInputProps()" :multiple="false">
                <span class="field field--file cap-field--file">Выберите или перетащите сюда файл <br> (максимум 25Мб)</span>
              </div>
            </div>
          </div>
          <div class="modal-bottom">
            <div class="buttons-block">
              <button type="button" class="button button--clear" @click="closeSubmitModal">Отменить</button>
              <button type="button" class="button" :key="submitDbl" :disabled="submitting" @click.once="submitVersion">Сохранить</button>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-overlay" id="delete-version">
        <div class="popup">
          <div class="modal-title title-h3">Удалить версию</div>
          <div class="modal-body">Вы действительно хотите удалить эту версию?</div>
          <div class="modal-bottom">
            <div class="buttons-block">
              <button type="button" class="button button--clear" @click="Shared.closeModal('#delete-version')">Отменить</button>
              <button type="button" class="button button--delete" :disabled="deleting" @click="deleteVersion">Удалить</button>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-overlay" id="version-history">
        <div class="popup popup-oy-auto cap-popup cap-popup-ail">
          <div class="modal-title title-h3">История изменения версии</div>
          <div class="modal-body cap-modal-body-left">
            <p v-if="historyLoading" class="cap-history-empty">Загрузка...</p>
            <p v-else-if="versionHistory.length === 0" class="cap-history-empty">Нет записей в истории</p>
            <div v-else class="cap-history-list">
              <div v-for="(item, index) in versionHistory" :key="index" class="file-report-history">
                <div class="line cap-history-list-item">
                  <div class="cap-history-list-left">
                    <span class="cap-history-list-data">{{ moment(item.timestamp).format('DD.MM.YYYY HH:mm') }}</span>
                    <span class="cap-history-list-fio">{{ item.person?.shortName }}</span>
                  </div>
                  <div class="value cap-history-list-status">{{ historyStatusName(item.newStatus) }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-bottom">
            <div class="buttons-block">
              <button type="button" class="button" @click="Shared.closeModal('#version-history')">Закрыть</button>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-overlay" id="download-report">
        <div class="popup cap-popup">
          <div class="modal-title title-h3">Скачать отчет</div>
          <div class="modal-body">
            <div class="cap-modal-comment-col">
              <label class="title-field">
                <span class="title-field-name">Комментарий при отправке версии</span>
                <textarea class="field cap-modal-comment cap-modal-comment-dash" rows="4" readonly :value="downloadReportSubmitText"></textarea>
              </label>
              <p class="cap-modal-note  cap-modal-note--left">*Комментарий был оставлен сотрудником РФРП при отправке версии на рассмотрение и не может быть изменён</p>
            </div>
            <div class="cap-modal-version cap-modal-version-left" v-if="downloadReportVersionLabel">
              {{ downloadReportVersionLabel }}, <a href="#" @click.prevent="openReport">открыть</a>
            </div>
          </div>
          <div class="modal-bottom">
            <div class="buttons-block">
              <button type="button" class="button button--clear" @click="Shared.closeModal('#download-report')">Закрыть</button>
              <button type="button" class="button" :disabled="downloading" @click="downloadReport">Скачать Отчет</button>
            </div>
          </div>
        </div>
      </div>
    </main>
    <MainFooter></MainFooter>
  </div>
</template>
